/**
 * Branding en la pantalla (DISENO §12; docs/activa/FASE-3-PLAN.md, «Branding»): los colores, las letras y los tonos, los
 * textos de ejemplo de la vista previa, el logo revisado antes de mandarlo, las palabras y la validación. El backend
 * vuelve a validar todo, y al logo lo reconoce por su contenido igual que acá.
 */

import type { Branding, IdentidadParaGuardar, Letra, TonoMarca } from "../types";

export const R_BRANDING = "/activa/branding";

export const COLORES = [
  { hex: "#111114", nombre: "Negro" },
  { hex: "#B3262D", nombre: "Rojo" },
  { hex: "#1F4E79", nombre: "Azul" },
  { hex: "#2F6B4F", nombre: "Verde" },
] as const;

export const LETRAS: { id: Letra; etiqueta: string; css: string }[] = [
  { id: "moderna", etiqueta: "Moderna", css: "'IBM Plex Sans', system-ui, sans-serif" },
  { id: "clasica", etiqueta: "Clásica", css: "Georgia, 'Times New Roman', serif" },
];

/** Los cuatro tonos, con el mail de ejemplo de la vista previa (el del diseño). */
export const TONOS: { id: TonoMarca; etiqueta: string; asunto: string; cuerpo: string }[] = [
  {
    id: "cercano",
    etiqueta: "Cercano",
    asunto: "Tu segundo pedido viene con 15%",
    cuerpo: "Hola Lucía, nos encantó tenerte la primera vez. Para tu segundo pedido tenés 15% con este código. Vale 7 días.",
  },
  {
    id: "divertido",
    etiqueta: "Divertido",
    asunto: "¿Repetimos, Lucía?",
    cuerpo: "Lucía, el primer pedido fue el ensayo. El segundo viene con 15% de regalo. Vale 7 días.",
  },
  {
    id: "elegante",
    etiqueta: "Elegante",
    asunto: "Una invitación para volver",
    cuerpo: "Lucía, será un gusto recibirte otra vez. Tu próximo pedido tiene un 15% de beneficio durante 7 días.",
  },
  { id: "directo", etiqueta: "Directo", asunto: "15% en tu segundo pedido", cuerpo: "Usá este código y tenés 15% en tu próximo pedido. Vale 7 días." },
];

export const CODIGO_DE_EJEMPLO = "SEGUNDA-X4M9-1026";
export const LOGO_MAX_BYTES = 300 * 1024;
export const MAX_PALABRAS = 30;
export const PALABRA_MAX = 40;
export const FIRMA_MAX = 80;

export const tonoDe = (t: TonoMarca) => TONOS.find((x) => x.id === t) ?? TONOS[0];
export const cssDeLetra = (l: Letra) => (LETRAS.find((x) => x.id === l) ?? LETRAS[0]).css;
/** El nombre que se ve en la vista previa cuando no hay logo. */
export const nombreParaMostrar = (marca: string) => marca.trim() || "Tu marca";

/* ── El color ────────────────────────────────────────────────────────────────────────────────── */

export const esColor = (v: unknown): v is string => typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v);

function luminancia(hex: string): number {
  const canal = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(1) + 0.7152 * canal(3) + 0.0722 * canal(5);
}

const contraste = (a: string, b: string) => {
  const [x, y] = [luminancia(a), luminancia(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
};

/** Blanco o casi negro, lo que se lea mejor arriba del color de la marca (como el backend). */
export function textoSobre(color: string): "#FFFFFF" | "#111114" {
  if (!esColor(color)) return "#FFFFFF";
  return contraste(color, "#FFFFFF") >= contraste(color, "#111114") ? "#FFFFFF" : "#111114";
}

/* ── El logo ─────────────────────────────────────────────────────────────────────────────────── */

const empiezaCon = (b: Uint8Array, desde: number, firma: number[]) => b.length >= desde + firma.length && firma.every((x, i) => b[desde + i] === x);
const ascii = (s: string) => [...s].map((c) => c.charCodeAt(0));

/** El tipo del logo por sus primeros bytes, como lo reconoce el backend; null si no es PNG, JPG ni WebP. */
export function tipoDelLogo(b: Uint8Array): "image/png" | "image/jpeg" | "image/webp" | null {
  if (empiezaCon(b, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]) && empiezaCon(b, 12, ascii("IHDR"))) return "image/png";
  if (empiezaCon(b, 0, [0xff, 0xd8, 0xff])) return "image/jpeg";
  if (empiezaCon(b, 0, ascii("RIFF")) && empiezaCon(b, 8, ascii("WEBP")) && empiezaCon(b, 12, ascii("VP8"))) return "image/webp";
  return null;
}

/** Redondeado para arriba: un archivo de 300,1 KB dice «301 KB», no «300 KB: el máximo es 300 KB». */
export const kb = (bytes: number) => `${Math.max(1, Math.ceil(bytes / 1024)).toLocaleString("es-AR")} KB`;

export const NO_ES_IMAGEN = "El logo tiene que ser una imagen PNG, JPG o WebP. SVG no, porque puede llevar código.";

/** Qué tiene de malo un archivo como logo, por su tamaño y sus primeros bytes; null si sirve. */
export function problemaDelLogo(bytes: number, cabecera: Uint8Array): string | null {
  if (bytes > LOGO_MAX_BYTES) return `El logo pesa ${kb(bytes)}: el máximo es 300 KB.`;
  if (!tipoDelLogo(cabecera)) return NO_ES_IMAGEN;
  return null;
}

function leerComo<T extends "arrayBuffer" | "dataUrl">(archivo: Blob, como: T): Promise<T extends "arrayBuffer" ? ArrayBuffer : string> {
  return new Promise((ok, mal) => {
    const r = new FileReader();
    r.onload = () => ok(r.result as never);
    r.onerror = () => mal(r.error ?? new Error("No se pudo leer el archivo"));
    if (como === "arrayBuffer") r.readAsArrayBuffer(archivo);
    else r.readAsDataURL(archivo);
  });
}

/** Lee el archivo elegido: si sirve como logo, su data URL para mandarlo; si no, por qué. */
export async function leerLogo(archivo: File): Promise<{ dataUrl: string } | { problema: string }> {
  if (archivo.size > LOGO_MAX_BYTES) return { problema: `El logo pesa ${kb(archivo.size)}: el máximo es 300 KB.` };
  try {
    const cabecera = new Uint8Array(await leerComo(archivo.slice(0, 16), "arrayBuffer"));
    const problema = problemaDelLogo(archivo.size, cabecera);
    if (problema) return { problema };
    return { dataUrl: await leerComo(archivo, "dataUrl") };
  } catch {
    return { problema: "No pudimos leer el archivo. Probá de nuevo." };
  }
}

/* ── La web y las palabras ───────────────────────────────────────────────────────────────────── */

/** A dónde lleva el botón del mail («tumarca.com.ar»), o null si lo escrito todavía no es una web. */
export function webParaMostrar(t: string): string | null {
  const v = t.trim();
  if (!v || /\s/.test(v)) return null;
  if (!/^https?:\/\//i.test(v) && /^[a-z][a-z0-9+.-]*:(?!\d)/i.test(v)) return null;
  let u: URL;
  try {
    u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
  } catch {
    return null;
  }
  if ((u.protocol !== "https:" && u.protocol !== "http:") || !u.hostname.includes(".") || u.hostname.endsWith(".") || u.username || u.password) return null;
  return u.hostname.replace(/^www\./, "");
}

const limpio = (s: string) => s.trim().replace(/\s+/g, " ");
const comparable = (s: string) =>
  limpio(s)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Qué tiene de malo una palabra nueva; null si entra. */
export function problemaDeLaPalabra(p: string, ya: string[]): string | null {
  const t = limpio(p);
  if (!t) return "Escribí la palabra.";
  if (t.length > PALABRA_MAX) return `Cada palabra va hasta ${PALABRA_MAX} letras.`;
  if (!/[\p{L}\p{N}]/u.test(t)) return "Escribí una palabra, con letras.";
  if (ya.some((x) => comparable(x) === comparable(t))) return `«${t}» ya está en la lista.`;
  if (ya.length >= MAX_PALABRAS) return `Podés cargar hasta ${MAX_PALABRAS} palabras.`;
  return null;
}

/**
 * Las palabras de la lista que aparecen en un texto, como las busca Reactivación: palabra entera, sin mirar acentos ni
 * mayúsculas («regalo» no salta dentro de «regalos»).
 */
export function palabrasEn(texto: string, palabras: string[]): string[] {
  const t = comparable(texto);
  return palabras.filter((p) => {
    const q = comparable(p).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return !!q && new RegExp(`(^|[^\\p{L}\\p{N}])${q}([^\\p{L}\\p{N}]|$)`, "u").test(t);
  });
}

/* ── El formulario ───────────────────────────────────────────────────────────────────────────── */

export interface Formulario {
  logo: string | null;
  /** el logo se cambió o se sacó: va en lo que se guarda */
  logoCambiado: boolean;
  web: string;
  color: string;
  letra: Letra;
  tono: TonoMarca;
  palabras: string[];
  firma: string;
}

export type Campo = "logo" | "web" | "color" | "palabras" | "firma";

export const formularioDe = (b: Branding): Formulario => ({
  logo: b.logo,
  logoCambiado: false,
  web: b.web,
  color: b.color,
  letra: b.letra,
  tono: b.tono,
  palabras: [...b.palabras],
  firma: b.firma,
});

export function validarFormulario(f: Formulario): { ok: true; identidad: IdentidadParaGuardar } | { ok: false; errores: Partial<Record<Campo, string>> } {
  const errores: Partial<Record<Campo, string>> = {};
  if (f.web.trim() && !webParaMostrar(f.web)) errores.web = "La web tiene que ser una dirección como https://tumarca.com.ar.";
  if (!esColor(f.color)) errores.color = "El color es un código como #1F4E79.";
  if (limpio(f.firma).length > FIRMA_MAX) errores.firma = `La firma va hasta ${FIRMA_MAX} letras.`;
  if (Object.keys(errores).length) return { ok: false, errores };
  return {
    ok: true,
    identidad: {
      web: f.web.trim(),
      color: f.color.toUpperCase(),
      letra: f.letra,
      tono: f.tono,
      palabras: [...f.palabras],
      firma: limpio(f.firma),
      ...(f.logoCambiado ? { logo: f.logo } : {}),
    },
  };
}
